import { useEffect, useMemo, useState } from "react";

import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControl,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
  MenuItem,
  Chip,
  SelectChangeEvent,
} from "@mui/material";

import { Article, Client, Container, Group, Unit } from "types";
import {
  calculateContainerVolumeWeight,
  getSuggestedContainers,
  volumeCalc,
} from "helpers";
import { isValidPositiveFloatNumber, toFloat } from "helpers/formatNumber";
import { STATUS_CODES } from "constants/index";
import CustomNumberInput from "components/CustomNumberInput";

type Props = {
  open: boolean;
  handleOpen: () => void;
  onCreate: (
    newArticle: Article,
    hasNewContainer: boolean,
    groups: number[],
  ) => void;
  containers: Container[];
  clients: Client[];
  units: Unit[];
  groups: Group[];
  onNewContainerCreate: (
    newContainer: Container,
  ) => Promise<{ status: any; data: Container }>;
};

type ContainerOption = Pick<Container, "id" | "description"> & {
  isSuggested?: boolean;
};

export default function AddArticleModal({
  open = true,
  handleOpen,
  onCreate,
  containers,
  clients,
  units,
  groups,
  onNewContainerCreate,
}: Props) {
  const defaultArticleValues: Omit<Article, "id"> = {
    client_id: 0,
    client: clients[0],
    code: "",
    container_id: 0,
    container: containers[0],
    unit_id: 0,
    unit: units[0],
    description: "",
    weight: 0,
    width: null,
    height: null,
    long: null,
    amount: 0,
    amount_per_container: 0,
    observation: "",
    require_fecha_vencimiento: false,
    is_standard_amount_per_container: true,
    presentation: null,
    fce: 1,
  };

  const defaultContainerValues: Omit<Container, "id"> = {
    description: "",
    m3_min: 0,
    m3_max: 0,
    volume_weight: 0,
    is_active: true,
  };

  const [articleData, setArticleData] =
    useState<Omit<Article, "id">>(defaultArticleValues);
  const [containerData, setContainerData] = useState<Omit<Container, "id">>(
    defaultContainerValues,
  );

  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);

  const [selectedClient, setSelectedClient] = useState<Pick<
    Client,
    "id" | "razon_social" | "cuit"
  > | null>(null);
  const [selectedContainer, setSelectedContainer] =
    useState<ContainerOption | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<Pick<
    Unit,
    "id" | "description"
  > | null>(null);

  const clientOptions = useMemo(
    () =>
      clients
        .filter((client) => client.is_active && client.parent_client_id != null)
        .map((client) => ({
          id: client.id,
          razon_social: `${client.code} - ${client.razon_social}`,
          cuit: client.cuit,
        })),
    [clients],
  );

  const groupOptions = useMemo(() => {
    return groups.filter((g) => g.client_id === articleData.client_id);
  }, [groups, articleData.client_id]);

  const articleVolume = useMemo(
    () =>
      volumeCalc(
        Number(articleData.width) || 0,
        Number(articleData.long) || 0,
        Number(articleData.height) || 0,
      ),
    [articleData.width, articleData.long, articleData.height],
  );

  const suggestedContainers = useMemo(
    () => getSuggestedContainers(containers, articleVolume),
    [containers, articleVolume],
  );

  const containerOptions = useMemo(() => {
    const activeContainers = containers.filter((container) => container.is_active);

    return activeContainers
      .map((container) => ({
        id: container.id,
        description: container.description,
        isSuggested: suggestedContainers.some(
          (suggestedContainer) => suggestedContainer.id === container.id,
        ),
      }))
      .sort((left, right) => {
        const suggestedDiff =
          Number(Boolean(right.isSuggested)) - Number(Boolean(left.isSuggested));

        if (suggestedDiff !== 0) return suggestedDiff;

        return left.description.localeCompare(right.description);
      })
      .concat({ id: 0, description: "Nuevo Contenedor", isSuggested: false });
  }, [containers, suggestedContainers]);

  useEffect(() => {
    setSelectedGroups([]);
  }, [articleData.client_id]);

  const handleChangeArticle = <
    K extends keyof Omit<Article, "id" | "container" | "client" | "unit">,
  >(
    name: K,
    value: string | Omit<Article, "id" | "container" | "client" | "unit">[K],
  ) => {
    setArticleData({
      ...articleData,
      [name]: value,
    });
  };

  const handleChangeContainer = <K extends keyof Omit<Container, "id">>(
    name: K,
    value: Omit<Container, "id">[K],
  ) => {
    setContainerData({
      ...containerData,
      [name]: value,
    });
  };

  const handleClose = () => {
    setSelectedClient(null);
    setSelectedContainer(null);
    setSelectedUnit(null);
    setArticleData(defaultArticleValues);
    setContainerData(defaultContainerValues);
    handleOpen();
  };

  const onSubmit = async (newArticle: Omit<Article, "id">) => {
    let container = containers.find(
      (container) => container.id === articleData.container_id,
    );
    const client = clients.find((client) => client.id === newArticle.client_id);
    const unit = units.find((unit) => unit.id === newArticle.unit_id);

    if (selectedContainer?.id === 0) {
      const response = await onNewContainerCreate(containerData as Container);
      if (response.status === STATUS_CODES.OK) {
        container = response.data;
        newArticle.container_id = response.data.id;
      }
    }
    if (!container || !client || !unit) return;

    onCreate(
      {
        ...articleData,
        container,
        client,
        unit,
      } as Article,
      selectedContainer?.id === 0 ? true : false,
      selectedGroups,
    );
  };

  const handleChangeGroups = (event: SelectChangeEvent<number[]>) => {
    const {
      target: { value },
    } = event;
    setSelectedGroups(
      typeof value === "string" ? value.split(",").map(Number) : value,
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: {
            xs: "95%",
            sm: "90%",
            md: selectedContainer?.id === 0 ? "60rem" : "40rem",
          },
          maxWidth: "95vw",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 2, md: 3 },
        }}
      >
        <Grid
          container
          component="form"
          spacing={2}
          onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            onSubmit(articleData);
            handleClose();
          }}
        >
          <Grid
            item
            xs={selectedContainer?.id === 0 ? 6 : 12}
            width={selectedContainer?.id === 0 ? "15rem" : "100%"}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  Nuevo Producto
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <TextField
                    required
                    label="Descripción"
                    fullWidth
                    onChange={(e) =>
                      handleChangeArticle("description", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <TextField
                    required
                    label="Código"
                    fullWidth
                    onChange={(e) =>
                      handleChangeArticle("code", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Peso"
                    required
                    value={articleData.weight}
                    onChange={(e) =>
                      handleChangeArticle("weight", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Cantidad por contenedor"
                    required
                    value={articleData.amount_per_container}
                    onChange={(e) =>
                      handleChangeArticle(
                        "amount_per_container",
                        e.target.value,
                      )
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Ancho"
                    value={articleData.width ?? ""}
                    allowOnlyPositive
                    onChange={(e) =>
                      handleChangeArticle("width", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Alto"
                    value={articleData.height ?? ""}
                    allowOnlyPositive
                    onChange={(e) =>
                      handleChangeArticle("height", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Largo"
                    value={articleData.long ?? ""}
                    allowOnlyPositive
                    onChange={(e) =>
                      handleChangeArticle("long", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} display={"flex"} columnGap={1}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Unidades por pallet"
                    required
                    value={articleData.units_per_pallet || ""}
                    onChange={(e) =>
                      handleChangeArticle(
                        "units_per_pallet",
                        toFloat(e.target.value),
                      )
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} display={"flex"} columnGap={1}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    id="unit"
                    autoComplete
                    value={selectedUnit}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    options={units.map((unit) => {
                      return {
                        id: unit.id,
                        description: unit.description,
                      };
                    })}
                    getOptionLabel={(option) => option.description}
                    onChange={(
                      e,
                      value: Pick<Unit, "id" | "description"> | null,
                    ) => {
                      if (!value) return;
                      handleChangeArticle("unit_id", value.id);
                      setSelectedUnit(value);
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Unidad Operativa"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    id="container"
                    autoComplete
                    value={selectedContainer}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) =>
                      option.isSuggested
                        ? `${option.description} • Sugerido`
                        : option.description
                    }
                    options={containerOptions}
                    onChange={(
                      e,
                      value: ContainerOption | null,
                    ) => {
                      if (!value) return;
                      handleChangeArticle("container_id", value.id);
                      setSelectedContainer(value);
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} required label="Contenedor" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Presentación"
                  value={articleData.presentation ?? ""}
                  onChange={(e) => handleChangeArticle("presentation", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {articleVolume > 0
                    ? suggestedContainers.length > 0
                      ? `Sugerencias por volumen (${articleVolume.toFixed(3)} m³): ${suggestedContainers
                          .map((container) => container.description)
                          .join(", ")}.`
                      : `Volumen calculado: ${articleVolume.toFixed(3)} m³. No hubo sugerencias, pero puede elegir cualquier contenedor activo.`
                    : "Las medidas del artículo son opcionales. Si completa ancho, alto y largo, el sistema sugerirá contenedores posibles."}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    id="client"
                    autoComplete
                    value={selectedClient}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) => option.razon_social}
                    options={clientOptions}
                    onChange={(
                      e,
                      value: Pick<
                        Client,
                        "id" | "razon_social" | "cuit"
                      > | null,
                    ) => {
                      if (!value) return;
                      handleChangeArticle("client_id", value.id);
                      setSelectedClient(value);
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} required label="Cliente" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <TextField
                    label="Observaciones"
                    fullWidth
                    minRows={2}
                    maxRows={4}
                    value={articleData.observation}
                    onChange={(e) =>
                      handleChangeArticle("observation", e.target.value)
                    }
                    placeholder="Observaciones internas del artículo (opcional)"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={articleData.require_fecha_vencimiento}
                      onChange={(e) =>
                        handleChangeArticle(
                          "require_fecha_vencimiento",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Requiere fecha de vencimiento"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={articleData.is_standard_amount_per_container}
                      onChange={(e) =>
                        handleChangeArticle(
                          "is_standard_amount_per_container",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Cantidad por contenedor estándar"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="FCE"
                    required
                    value={articleData.fce}
                    onChange={(e) => handleChangeArticle("fce", e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="none">
                  <InputLabel id="groups-multiple-label">Grupos</InputLabel>
                  <Select
                    labelId="groups-multiple-label"
                    id="groups-multiple-chip"
                    multiple
                    value={selectedGroups}
                    onChange={handleChangeGroups}
                    input={
                      <OutlinedInput
                        id="select-groups-multiple-chip"
                        label="Grupos"
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const group = groups.find((g) => g.id === value);
                          return (
                            <Chip key={value} label={group?.description} />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {groupOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          {selectedContainer?.id === 0 && (
            <Grid item xs={6} width="15rem">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom>
                    Nuevo Contenedor
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descripción"
                    fullWidth
                    required
                    onChange={(e) =>
                      handleChangeContainer("description", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomNumberInput
                    label="M3 mínimo"
                    value={containerData.m3_min}
                    required
                    allowOnlyPositive
                    onChange={(e) =>
                      handleChangeContainer("m3_min", toFloat(e.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomNumberInput
                    label="M3 máximo"
                    value={containerData.m3_max}
                    required
                    allowOnlyPositive
                    onChange={(e) =>
                      handleChangeContainer("m3_max", toFloat(e.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Peso volumétrico"
                    fullWidth
                    disabled
                    value={calculateContainerVolumeWeight(containerData.m3_max)}
                    helperText="Calculado automáticamente: m3_max × 350"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} display="flex" justifyContent="end" gap={2} pt={2}>
            <Button type="button" variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !articleData.client_id ||
                (selectedContainer?.id !== 0 && !articleData.container_id) ||
                !articleData.unit_id ||
                !articleData.description ||
                !articleData.code ||
                !isValidPositiveFloatNumber(articleData.weight.toString()) ||
                !isValidPositiveFloatNumber(
                  articleData.amount_per_container.toString(),
                ) ||
                (selectedContainer?.id === 0 &&
                  (!containerData.description ||
                    !isValidPositiveFloatNumber(
                      containerData.m3_min?.toString() || "",
                    ) ||
                    !isValidPositiveFloatNumber(
                      containerData.m3_max?.toString() || "",
                    ) ||
                    Number(containerData.m3_max) < Number(containerData.m3_min))) ||
                !articleData.fce
              }
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
